import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const planBadge = (status, plan) => {
  if (status === "active") return { cls: "bg-emerald-500/15 text-emerald-400", label: plan };
  if (status === "trial") return { cls: "bg-amber-500/15 text-amber-400", label: "Trial" };
  return { cls: "bg-stone-500/15 text-stone-400", label: plan };
};

export default function AdminClients() {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data)); }, []);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const s = q.toLowerCase();
    return clients.filter((c) =>
      c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) ||
      (c.restaurant?.name || "").toLowerCase().includes(s));
  }, [clients, q]);

  if (!clients) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div data-testid="admin-clients-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-white">Client Accounts</h1>
          <p className="text-stone-400 mt-1">{clients.length} restaurant{clients.length !== 1 ? "s" : ""} on the platform.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="admin-client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients..."
            className="pl-9 bg-[#1A1814] border-white/10 text-white placeholder:text-stone-500" />
        </div>
      </div>

      <div className="bg-[#1A1814] border border-white/10 rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold text-stone-500 border-b border-white/10">
          <div className="col-span-4">Restaurant</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2 text-right">Revenue</div>
          <div className="col-span-1 text-right">Orders</div>
          <div className="col-span-2 text-right">Staff / Menu</div>
          <div className="col-span-1" />
        </div>
        <div className="divide-y divide-white/5">
          {filtered.map((c) => {
            const b = planBadge(c.subscription.status, c.subscription.plan);
            return (
              <button key={c.id} data-testid={`admin-client-row-${c.id}`} onClick={() => navigate(`/admin/clients/${c.id}`)}
                className="w-full grid grid-cols-2 md:grid-cols-12 gap-4 px-5 py-4 items-center text-left hover:bg-white/5 transition-colors">
                <div className="col-span-2 md:col-span-4 flex items-center gap-3 min-w-0">
                  <span className="h-9 w-9 rounded-lg bg-white/5 flex items-center justify-center font-heading font-semibold text-white shrink-0">{c.restaurant?.name?.charAt(0) || "?"}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{c.restaurant?.name || "—"}</p>
                    <p className="text-xs text-stone-500 truncate">{c.email}</p>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${b.cls}`}>{b.label}</span>
                  {c.subscription.status === "trial" && <span className="text-xs text-stone-500 ml-2">{c.subscription.trial_days_left}d left</span>}
                </div>
                <div className="md:col-span-2 text-right font-medium text-white">{money(c.revenue)}</div>
                <div className="hidden md:block col-span-1 text-right text-stone-400">{c.orders_count}</div>
                <div className="hidden md:block col-span-2 text-right text-stone-400">{c.staff_count} / {c.menu_count}</div>
                <div className="hidden md:flex col-span-1 justify-end"><ChevronRight className="h-4 w-4 text-stone-500" /></div>
              </button>
            );
          })}
          {filtered.length === 0 && <p className="text-center text-stone-500 py-12">No clients match your search.</p>}
        </div>
      </div>
    </div>
  );
}
